/**
 * MyDocumentsPage — Document History & Orders
 *
 * Lists all saved AI-generated and user-edited document drafts
 * from Document Library, Resolutions, and Meeting Minutes pages.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FiSearch, FiFolder, FiCopy, FiFileText, FiClock, FiCheckSquare, FiBarChart2 } from 'react-icons/fi';
import AppLayout from '../components/layout/AppLayout';
import Button from '../components/common/Button';
import PageHeader from '../components/common/PageHeader';
import Chip from '../components/common/Chip';
import EmptyState from '../components/common/EmptyState';
import SegmentedControl from '../components/common/SegmentedControl';
import { IlloDocuments, IlloSearch } from '../components/common/Illustrations';
import { Select as FormSelect, inputClass } from '../components/common/FormControls';
import DocumentDraftModal from '../components/drafts/DocumentDraftModal';
import ResolutionModal from '../components/drafts/ResolutionModal';
import ShareDraftModal from '../components/drafts/ShareDraftModal';
import SignoffModal from '../components/drafts/SignoffModal';
import { statusView } from '../utils/signoffView';
import ConfirmDialog from '../components/common/ConfirmDialog';
import HoverPopover from '../components/common/HoverPopover';
import Icon from '../components/common/Icon';
import NavGlyph from '../components/common/NavGlyph';
import { RESOLUTIONS } from '../data/resolutions';
import { DOCUMENTS } from '../data/documents';
import { REPORT_TEMPLATES } from '../data/reportTemplates';
import { draftsAPI, professionalAssignmentAPI } from '../services/api';
import { deleteDraft as cacheDelete } from '../services/draftCacheService';
import { getView, setView, hasView, clearViewsByPrefix } from '../services/viewCache';
import { useUI } from '../context/UIContext';
import { useApp } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import { ROUTES } from '../constants/constants';

/* ── badge palette — one cohesive earthy family (navy · teal · gold · taupe),
   soft tinted fills with a same-family border. Deliberately low-saturation so
   the table reads as a premium compliance product, not a swatch of bright chips. */
const TYPE_BADGE = {
  minutes: { bg: '#edeef3', color: '#3a4a66', border: '#d8dce8', label: 'Minutes' },
  resolution: { bg: '#edeef3', color: '#3a4a66', border: '#d8dce8', label: 'Resolution' },
  document: { bg: '#edeef3', color: '#3a4a66', border: '#d8dce8', label: 'Document' },
  report: { bg: '#edeef3', color: '#3a4a66', border: '#d8dce8', label: 'Report' },
};

const TYPE_ICONS = {
  minutes: FiClock,
  resolution: FiCheckSquare,
  document: FiFileText,
  report: FiBarChart2,
};

/* Per-item glyphs, inherited from each source page's catalog so a saved draft
   shows the exact icon it has on Resolutions / Document Library / Report
   Templates — not a generic by-type fallback. Keyed by the same documentId the
   draft carries; the emoji is passed through the same render-time converter each
   source page uses (Resolutions → NavGlyph, Document Library / Reports → Icon). */
const RES_ICON_BY_ID = Object.fromEntries(RESOLUTIONS.map((r) => [r.id, r.icon]));
const DOC_ICON_BY_ID = Object.fromEntries(DOCUMENTS.map((d) => [d.id, d.icon]));
const REPORT_ICON_BY_ID = Object.fromEntries(REPORT_TEMPLATES.map((r) => [r.id, r.icon]));

function DocGlyph({ type, id, size = 16 }) {
  if (type === 'resolution' && RES_ICON_BY_ID[id]) {
    return <NavGlyph icon={RES_ICON_BY_ID[id]} size={size} />;
  }
  if (type === 'document' && DOC_ICON_BY_ID[id]) {
    return <Icon name={DOC_ICON_BY_ID[id]} size={size} />;
  }
  if (type === 'report' && REPORT_ICON_BY_ID[id]) {
    return <Icon name={REPORT_ICON_BY_ID[id]} size={size} />;
  }
  const Fallback = TYPE_ICONS[type] || FiFileText;
  return <Fallback size={size} />;
}

const VISIBILITY_BADGE = {
  public: {
    bg: '#f3ebd7',
    color: '#836016',
    border: '#e7d7ad',
    label: 'Public',
    description:
      "Visible to the corporate owner and every professional assigned to this company (subject to each assignment's access-level rules).",
  },
  private: {
    bg: '#f3ebd7',
    color: '#836016',
    border: '#e7d7ad',
    label: 'Private',
    description:
      "Visible only to the corporate owner and the specific user this draft was assigned to. Other professionals on the same company can't see it.",
  },
  restricted: {
    bg: '#f3ebd7',
    color: '#836016',
    border: '#e7d7ad',
    label: 'Restricted',
    description:
      'Shared with a specific set of people — the creator and whoever it has been shared with. Click to switch it to Public or Private.',
  },
  personal: {
    bg: '#f3ebd7',
    color: '#836016',
    border: '#e7d7ad',
    label: 'Personal',
    description:
      "No company attached. Visible only to you, and locked — personal drafts can't be changed to Public or Private.",
  },
};

/* ── format date for table ────────────────────────────────────────── */
function formatDate(dateStr) {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-IN', { year: 'numeric', month: '2-digit', day: '2-digit' });
}

/* ═══════════════════════════════════════════════════════════════════ */

export default function MyDocumentsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { showToast } = useUI();
  const { companies } = useApp();
  const { user } = useAuth();

  /* ── state ──────────────────────────────────────────────────────── */
  // Filters, search and page come first: they key the stale-while-revalidate
  // cache below, so a different view is a clean miss rather than stale bleed.
  const [activeFilter, setActiveFilter] = useState('all');
  const [selectedCompany, setSelectedCompany] = useState('');   // companyCin filter
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  // This route unmounts on navigation; without a cache, coming back refetched
  // from zero and flashed skeleton rows over drafts the user saw seconds ago.
  // Cache the last loaded page + the stats roll-up module-side and render them
  // instantly on return (services/viewCache). The list key folds in every param
  // the results depend on; stats are filter-independent, so one stable key.
  const listKey = `myDocuments:list:${page}|${activeFilter}|${selectedCompany}|${search.trim()}`;
  const STATS_KEY = 'myDocuments:stats';
  const ASSIGN_KEY = 'myDocuments:assignments';

  const [drafts, setDrafts] = useState(() => getView(listKey)?.drafts ?? []);
  const [pagination, setPagination] = useState(() => getView(listKey)?.pagination ?? { pages: 1, total: 0, hasNext: false, hasPrev: false });
  const [allDraftsForStats, setAllDraftsForStats] = useState(() => getView(STATS_KEY) ?? []);
  // Full skeleton only for a true first load — a cached view renders straight away.
  const [loading, setLoading] = useState(() => !hasView(listKey));

  const docStats = useMemo(() => {
    const total = allDraftsForStats.length;
    const ai = allDraftsForStats.filter(d => d.docSource === 'ai').length;
    const user = total - ai;
    return { total, ai, user };
  }, [allDraftsForStats]);
  const [deletingId, setDeletingId] = useState(null);
  const [loadingViewId, setLoadingViewId] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null); // draft object or null

  // Modal state for viewing documents/resolutions in-place
  const [viewingDoc, setViewingDoc] = useState(null);     // { draft, mode: 'document' | 'resolution' }
  // Assignment state for professional users
  const [assignments, setAssignments] = useState(() => getView(ASSIGN_KEY) ?? []);
  const [loadingAssignments, setLoadingAssignments] = useState(false);

  // Share modal state (professional picker + access level + revoke list live
  // inside ShareDraftModal itself).
  const [sharingDraft, setSharingDraft] = useState(null);
  // Review and sign dialog (maker / checker / signing professional).
  const [signingDraft, setSigningDraft] = useState(null);

  /* ── User type detection ───────────────────────────────────────────── */
  const isProfessional = useMemo(() => {
    return user?.roleName === 'professional' || user?.role?.name === 'professional';
  }, [user]);

  const isCorporate = useMemo(() => {
    return user?.roleName === 'corporate' || user?.role?.name === 'corporate';
  }, [user]);

  /* ── Load assignments for professional users ────────────────────────── */
  const fetchAssignments = useCallback(async () => {
    if (!isProfessional) return;

    // Spinner only on a true first load; a cached list refreshes silently.
    if (!hasView(ASSIGN_KEY)) setLoadingAssignments(true);
    try {
      const res = await professionalAssignmentAPI.getMyProfessionalAssignments({
        status: 'active',
        professionalStatus: 'accepted'
      });
      if (res?.success) {
        const list = res.data?.assignments || [];
        setAssignments(list);
        setView(ASSIGN_KEY, list);
      }
    } catch (err) {
      // Keep the cached assignments on a failed refresh.
      console.error('Failed to load assignments:', err);
    } finally {
      setLoadingAssignments(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isProfessional]);

  useEffect(() => { fetchAssignments(); }, [fetchAssignments]);

  /* ── Toggle document visibility ───────────────────────────────────── */
  const handleVisibilityToggle = useCallback(async (draftId, currentVisibility) => {
    try {
      if (currentVisibility === 'personal') {
        showToast("Personal drafts can't change visibility.", 'info');
        return;
      }

      // public / private / restricted all flip here — restricted lands on
      // public, then toggles normally. The backend accepts restricted →
      // public/private (only personal stays locked).
      const newVisibility = currentVisibility === 'public' ? 'private' : 'public';

      const res = await draftsAPI.updateDraft(draftId, { visibility: newVisibility });

      if (res?.success) {
        showToast(`Document visibility changed to ${newVisibility}`, 'success');
        // Update local state
        setDrafts(prev => prev.map(draft =>
          draft._id === draftId ? { ...draft, visibility: newVisibility } : draft
        ));
      } else {
        showToast(res?.message || 'Failed to update visibility', 'error');
      }
    } catch (err) {
      showToast(err.message || 'Failed to update visibility', 'error');
    }
  }, [showToast]);

  /* ── Share modal callback: reflect visibility flips locally ───────── */
  const handleShareChanged = useCallback((draftId, { visibility } = {}) => {
    if (!visibility) return;
    setDrafts((prev) => prev.map((d) => (d._id === draftId ? { ...d, visibility } : d)));
  }, []);

  /* ── Sign-off callback: show the new status on the row ─────────────── */
  const handleSignoffChanged = useCallback((draftId, signoff) => {
    setDrafts((prev) => prev.map((d) => (d._id === draftId ? { ...d, signoff } : d)));
    setSigningDraft((cur) => (cur && cur._id === draftId ? { ...cur, signoff } : cur));
  }, []);

  /* ── Get accessible companies for professional users ───────────────── */
  const accessibleCompanies = useMemo(() => {
    if (!isProfessional || assignments.length === 0) return [];

    const accessibleCins = new Set();
    assignments.forEach(assignment => {
      if (assignment.assignmentScope === 'all_companies') {
        // If assigned to all companies, professional has access to all
        (companies || []).forEach(c => {
          const cin = c.cin || c.CIN || '';
          if (cin) accessibleCins.add(cin);
        });
      } else {
        // Specific companies assignment
        assignment.assignedCompanies.forEach(ac => {
          if (ac.company && ac.company.cin) {
            accessibleCins.add(ac.company.cin);
          }
        });
      }
    });

    return Array.from(accessibleCins);
  }, [isProfessional, assignments, companies]);

  /* ── Filter drafts based on assignments for professional users ─────── */
  const filteredDrafts = useMemo(() => {
    if (!isProfessional) {
      return drafts; // No filtering for corporate users
    }

    // For professional users, the backend already handles filtering based on assignments
    // But we can add additional client-side filtering if needed
    return drafts;
  }, [drafts, isProfessional]);

  /* ── derive unique companies from loaded drafts + user companies ── */
  const companyOptions = useMemo(() => {
    const map = new Map();

    // For professional users, only show assigned companies
    const companiesToUse = isProfessional ?
      companies.filter(c => accessibleCompanies.includes(c.cin || c.CIN || '')) :
      companies;

    // From user's companies list (AppContext)
    companiesToUse.forEach((c) => {
      const cin = c.cin || c.CIN || '';
      if (cin) map.set(cin, c.name || c.companyName || cin);
    });

    // Also add any company from drafts that might not be in the list
    const draftsToUse = isProfessional ? filteredDrafts : drafts;
    draftsToUse.forEach((d) => {
      if (d.companyCin && !map.has(d.companyCin)) {
        map.set(d.companyCin, d.companyName || d.companyCin);
      }
    });

    return Array.from(map, ([cin, name]) => ({ cin, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [companies, drafts, isProfessional, accessibleCompanies, filteredDrafts]);

  /* ── load drafts ────────────────────────────────────────────────── */
  const fetchDrafts = useCallback(async () => {
    // Full skeleton only when this exact view has never loaded; a cached view
    // (revisit, or a filter you've already seen) refreshes silently underneath.
    if (!hasView(listKey)) setLoading(true);
    try {
      const params = { page, limit: 15 };
      if (activeFilter === 'ai') params.source = 'ai';
      if (activeFilter === 'user-edited') params.source = 'user-edited';
      if (selectedCompany) params.companyCin = selectedCompany;
      if (search.trim()) params.search = search.trim();

      // The filtered page and the stats roll-up are independent requests — fire
      // them together rather than one after the other (was a waterfall).
      const [res, statsRes] = await Promise.all([
        draftsAPI.getAll(params),
        draftsAPI.getAll({ limit: 10000 }),
      ]);

      if (res?.success) {
        const list = res.data || [];
        const pg = res.pagination || { pages: 1, total: 0, hasNext: false, hasPrev: false };
        setDrafts(list);
        setPagination(pg);
        setView(listKey, { drafts: list, pagination: pg });
      }
      if (statsRes?.success) {
        const all = statsRes.data || [];
        setAllDraftsForStats(all);
        setView(STATS_KEY, all);
      }
    } catch {
      // Keep the cached list on a failed refresh; a true first load simply has
      // nothing to show yet (the API may not have drafts).
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, activeFilter, selectedCompany, search]);

  useEffect(() => { fetchDrafts(); }, [fetchDrafts]);

  // Reset page when filter, company, or search changes
  const handleFilterChange = (filterId) => {
    setActiveFilter(filterId);
    setPage(1);
  };
  const handleCompanyChange = (cin) => {
    setSelectedCompany(cin);
    setPage(1);
  };

  /* ── debounced search ───────────────────────────────────────────── */
  const [searchInput, setSearchInput] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput);
      setPage(1);
    }, 350);
    return () => clearTimeout(timer);
  }, [searchInput]);

  /* ── handlers ───────────────────────────────────────────────────── */
  const handleView = useCallback(async (draft) => {
    // Minutes / Report drafts live on their own full-page editors — give the
    // redirect an explicit cue so the jump isn't silent.
    if (draft.pageOrigin === 'minutes') {
      showToast('Opening in Minutes…', 'info');
      navigate(ROUTES.MINUTES, { state: { draftId: draft._id } });
      return;
    }
    if (draft.pageOrigin === 'report-generator') {
      showToast('Opening in Report Generator…', 'info');
      navigate(ROUTES.REPORT_GENERATOR, { state: { draftId: draft._id } });
      return;
    }

    // Document / resolution drafts open in-place. Open the modal INSTANTLY in a
    // loading state (skeleton) using the list row's data, then hydrate it once
    // the full draft (docHtml) arrives — so the click feels immediate.
    const mode = draft.pageOrigin === 'resolutions' ? 'resolution' : 'document';
    setViewingDoc({ draft, mode, loading: true });
    setLoadingViewId(draft._id);
    try {
      const res = await draftsAPI.getById(draft._id);
      if (!res?.success || !res.data) {
        showToast('Failed to load draft', 'error');
        setViewingDoc(null);
        return;
      }
      setViewingDoc({ draft: res.data, mode, loading: false });
    } catch (err) {
      showToast(err.message || 'Failed to load draft', 'error');
      setViewingDoc(null);
    } finally {
      setLoadingViewId(null);
    }
  }, [navigate, showToast]);

  const handleDeleteClick = useCallback((draft) => {
    // A professional can delete only drafts they created — shared/granted
    // visibility lets them view, never remove. Corporate keeps its own scope.
    const myId = String(user?.id || user?._id || '');
    const isMyDraft = String(draft.user?._id || draft.user || '') === myId;
    const canDelete = isCorporate || (isProfessional && isMyDraft);

    if (!canDelete) {
      showToast('You can only delete documents you created.', 'error');
      return;
    }

    setConfirmDelete(draft);
  }, [isCorporate, isProfessional, user, showToast]);

  const executeDelete = useCallback(async () => {
    if (!confirmDelete) return;
    setDeletingId(confirmDelete._id);
    try {
      await draftsAPI.delete(confirmDelete._id);
      cacheDelete(confirmDelete.documentId, confirmDelete.companyCin).catch(() => { });
      setDrafts((prev) => prev.filter((d) => d._id !== confirmDelete._id));
      // Drop the cached list/stats views so the deleted row can't resurface from
      // cache on the next visit; the mount refresh then repopulates them.
      clearViewsByPrefix('myDocuments:');
      showToast('Draft deleted', 'success');
      setConfirmDelete(null);
    } catch (err) {
      showToast(err.message || 'Failed to delete draft', 'error');
    } finally {
      setDeletingId(null);
    }
  }, [confirmDelete, showToast]);

  const closeViewModal = useCallback(() => {
    setViewingDoc(null);
    fetchDrafts();
  }, [fetchDrafts]);

  // Deep-link from Spotlight ("Open RES-XXXXXX"): the ref arrives in nav state.
  // Resolve it via the by-ref endpoint (admins included) and open the draft.
  // Guarded so it fires once, and the state is cleared so back/refresh won't
  // re-trigger it.
  const lastRefHandled = useRef(null);
  useEffect(() => {
    const ref = location.state?.openDraftRef;
    // Reset when the state clears so the SAME ref can be opened again later.
    if (!ref) { lastRefHandled.current = null; return; }
    if (lastRefHandled.current === ref) return; // ignore StrictMode re-invoke
    lastRefHandled.current = ref;
    navigate(location.pathname, { replace: true, state: {} });
    (async () => {
      try {
        const res = await draftsAPI.getByRef(ref);
        if (res?.success && res.data) {
          handleView(res.data);
        } else {
          showToast(`No document found for ${ref}`, 'error');
        }
      } catch (err) {
        showToast(err?.message || `No document found for ${ref}`, 'error');
      }
    })();
  }, [location.state, location.pathname, navigate, handleView, showToast]);

  /* ── render ─────────────────────────────────────────────────────── */
  return (
    <AppLayout pageTitle="My Documents / History">
      {/* ── Page header ───────────────────────────────────────────── */}
      <PageHeader
        title="Document History & Orders"
        subtitle="View and manage all your drafted minutes, resolutions, and legal forms."
      />

      {/* ── Source filter tabs ─────────────────────────────────────── */}
      <div className="mb-5">
        <SegmentedControl
          value={activeFilter}
          onChange={handleFilterChange}
          options={[
            { key: 'all', label: 'All Documents', count: docStats.total },
            { key: 'ai', label: 'AI Generated', count: docStats.ai },
            { key: 'user-edited', label: 'User Drafted', count: docStats.user },
          ]}
        />
      </div>

      {/* ── Company filter + Search Flexbar ─────────────────────── */}
      <div className="flex items-center justify-between gap-4 mb-5 flex-wrap sm:flex-nowrap">
        <div className="relative flex-1 min-w-[240px]">
          <FormSelect
            value={selectedCompany}
            onChange={handleCompanyChange}
            placeholder="All Companies"
            options={[{ value: '', label: 'All Companies' }, ...companyOptions.map((c) => ({ value: c.cin, label: c.name }))]}
          />
        </div>

        <div className="relative shrink-0 w-full sm:w-[280px]">
          <FiSearch size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: '#7a7060' }} />
          <input
            className={`${inputClass} !pl-[38px]`}
            style={{ width: '100%', borderRadius: '12px', border: '1.5px solid #ddd6c6', background: '#ffffff', fontFamily: "'Outfit', sans-serif" }}
            placeholder="Search by title or company..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </div>
      </div>

      {/* ── Responsive row list / three-state empty ──────────────── */}
      {(!loading && !loadingAssignments && (isProfessional ? filteredDrafts.length : drafts.length) === 0) ? (
        (activeFilter !== 'all' || selectedCompany || search.trim()) ? (
          <EmptyState
            variant="filter"
            illustration={<IlloSearch />}
            title="No documents match"
            subtitle="Try a different search, switch the source tab, or clear the company filter."
            action={(
              <Button
                variant="outline"
                size="sm"
                onClick={() => { setActiveFilter('all'); setSelectedCompany(''); setSearchInput(''); setPage(1); }}
              >
                Clear filters
              </Button>
            )}
          />
        ) : isProfessional ? (
          <EmptyState
            variant="hero"
            illustration={<IlloDocuments />}
            title="No documents shared with you yet"
            subtitle="When a company owner assigns you to their drafts, they'll appear here for you to view and work on."
          />
        ) : (
          <EmptyState
            variant="hero"
            illustration={<IlloDocuments />}
            title="Your document history starts here"
            subtitle="Every draft you generate from Minutes, Resolutions, or the Document Library is saved here — ready to reopen, share with your professional, or download."
            action={(
              <Button
                variant="gold"
                size="sm"
                className="!h-[38px] !rounded-xl"
                leftIcon={<FiFolder size={15} />}
                onClick={() => navigate(ROUTES.DOC_LIBRARY)}
              >
                Draft your first document
              </Button>
            )}
          />
        )
      ) : (
        <div className="space-y-3">
          {loading || loadingAssignments ? (
            Array.from({ length: 5 }).map((_, i) => (
              <div
                key={`row-skeleton-${i}`}
                className="bg-white border border-[#e7e2d8] rounded-[16px] p-4 flex items-center justify-between animate-pulse"
              >
                <div className="flex items-center gap-3.5 flex-1 min-w-0 pr-4">
                  <div className="w-10 h-10 rounded-[12px] bg-[#e7e2d8]" />
                  <div className="space-y-2 flex-1 min-w-0">
                    <div className="h-3.5 bg-[#e7e2d8] rounded w-[68%]" />
                    <div className="h-2.5 bg-[#ece8df] rounded w-[38%]" />
                  </div>
                </div>
                <div className="flex items-center gap-4 shrink-0">
                  <div className="h-5 w-16 bg-[#e7e2d8] rounded-lg" />
                  <div className="h-5 w-16 bg-[#e7e2d8] rounded-lg" />
                  <div className="h-7 w-24 bg-[#ece8df] rounded-[8px]" />
                </div>
              </div>
            ))
          ) : (
            (isProfessional ? filteredDrafts : drafts).map((draft) => {
              const typeBadge = TYPE_BADGE[draft.documentType] || TYPE_BADGE.document;
              const visibilityBadge = VISIBILITY_BADGE[draft.visibility] || VISIBILITY_BADGE.private;
              const isDeleting = deletingId === draft._id;
              const isMyDraft =
                String(draft.user?._id || draft.user || '') ===
                String(user?.id || user?._id || '');

              return (
                <div
                  key={draft._id}
                  className="cs-resolution-card group rounded-[16px] p-4 flex items-center justify-between gap-4 flex-wrap md:flex-nowrap"
                  style={{
                    opacity: isDeleting ? 0.5 : 1,
                  }}
                >
                  {/* Left profile: Icon + title details */}
                  <div className="flex items-center gap-3.5 flex-1 min-w-0">
                    <span
                      className="shrink-0 grid place-items-center w-10 h-10 rounded-[12px] border transition-all duration-300 group-hover:scale-105"
                      style={{ background: typeBadge.bg, color: typeBadge.color, borderColor: typeBadge.border }}
                    >
                      <DocGlyph type={draft.documentType} id={draft.documentId} size={16} />
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span
                          className="font-bold text-[13.5px] text-[#08111f] hover:text-[#8a6a1f] transition-colors cursor-pointer"
                          onClick={() => handleView(draft)}
                          style={{ fontFamily: "'Outfit', sans-serif", letterSpacing: '-0.01em' }}
                        >
                          {draft.documentTitle}
                        </span>
                        {draft.refNumber && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (!navigator.clipboard) {
                                showToast('Clipboard unavailable', 'error');
                                return;
                              }
                              navigator.clipboard
                                .writeText(draft.refNumber)
                                .then(() => showToast(`Copied ${draft.refNumber}`, 'success'))
                                .catch(() => showToast('Could not copy reference', 'error'));
                            }}
                            title="Copy reference number"
                            className="inline-flex items-center gap-1 px-[6px] py-[1.5px] rounded-[5px] text-[9.5px] font-bold leading-[1.3] transition-colors hover:bg-[#ece5d6]"
                            style={{ fontFamily: "'JetBrains Mono', monospace", color: '#7a7060', background: '#f5f0e8' }}
                          >
                            {draft.refNumber}
                            <FiCopy size={9} aria-hidden="true" style={{ opacity: 0.55 }} />
                          </button>
                        )}
                      </div>

                      <div className="flex items-center gap-2 mt-1.5 text-[11px] text-[#7a7060] flex-wrap leading-none" style={{ fontFamily: "'Outfit', sans-serif" }}>
                        <span className="font-bold text-[#145f5f]">{draft.companyName || 'No Company'}</span>
                        <span className="text-[#ddd6c6]">•</span>
                        <span>Created {formatDate(draft.updatedAt || draft.createdAt)}</span>
                        <span className="text-[#ddd6c6]">•</span>
                        <span className="font-medium">
                          {draft.professionalCreator?.fullName || draft.professionalCreator?.name ?
                            (draft.professionalCreator.fullName || draft.professionalCreator.name) +
                            (draft.createdOnBehalfOf?.fullName || draft.createdOnBehalfOf?.name ?
                              ` (on behalf of ${draft.createdOnBehalfOf.fullName || draft.createdOnBehalfOf.name})` : '') :
                            draft.createdOnBehalfOf?.fullName || draft.createdOnBehalfOf?.name ?
                              `Professional (on behalf of ${draft.createdOnBehalfOf.fullName || draft.createdOnBehalfOf.name})` :
                              draft.user?.fullName || draft.user?.name || 'Self'
                          }
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Middle actions: badges & sharing levels */}
                  <div className="flex items-center gap-3 shrink-0 flex-wrap sm:flex-nowrap">
                    {/* Document Type */}
                    <Chip bg={typeBadge.bg} color={typeBadge.color}>
                      {typeBadge.label}
                    </Chip>

                    {/* Visibility */}
                    <span>
                      {isCorporate && draft.visibility !== 'personal' ? (
                        <HoverPopover
                          width={280}
                          content={
                            <>
                              <div className="mb-1.5 font-bold" style={{ color: visibilityBadge.color }}>
                                    {visibilityBadge.label}
                              </div>
                              <div className="text-[11.5px] leading-relaxed">{visibilityBadge.description}</div>
                              <div
                                className="mt-2 pt-2 text-[10px] italic border-t"
                                style={{ color: '#7a7060', borderColor: '#f0ebe0' }}
                              >
                                Click to switch to {draft.visibility === 'public' ? 'Private' : 'Public'}.
                              </div>
                            </>
                          }
                        >
                          <button
                            className="inline-flex items-center rounded-full px-2.5 py-[3px] text-[11px] font-bold leading-[1.2] cursor-pointer transition-all hover:scale-105 active:scale-95 border"
                            style={{ background: visibilityBadge.bg, color: visibilityBadge.color, borderColor: visibilityBadge.border }}
                            onClick={() => handleVisibilityToggle(draft._id, draft.visibility || 'private')}
                          >
                            {visibilityBadge.label}
                          </button>
                        </HoverPopover>
                      ) : (
                        <HoverPopover
                          width={280}
                          content={
                            <>
                              <div className="mb-1.5 font-bold" style={{ color: visibilityBadge.color }}>
                                    {visibilityBadge.label}
                              </div>
                              <div className="text-[11.5px] leading-relaxed">{visibilityBadge.description}</div>
                            </>
                          }
                        >
                          <Chip bg={visibilityBadge.bg} color={visibilityBadge.color}>
                            {visibilityBadge.label}
                          </Chip>
                        </HoverPopover>
                      )}
                    </span>

                    {/* Access level (RO, AD, RW) */}
                    {isProfessional && draft.accessLevel && (
                      <Chip
                        bg={draft.accessLevel === 'admin' ? '#e8efe5' : draft.accessLevel === 'read_write' ? '#f3ebd7' : '#edeef3'}
                        color={draft.accessLevel === 'admin' ? '#4a6b3f' : draft.accessLevel === 'read_write' ? '#8a6a1f' : '#3a4a66'}
                        title={`Your access level: ${draft.accessLevel.replace('_', ' ')}`}
                      >
                        {draft.accessLevel === 'read_write' ? 'RW' : draft.accessLevel === 'admin' ? 'AD' : 'RO'}
                      </Chip>
                    )}
                    {/* Shared-with-me marker: this row exists because the owner
                        shared it, not because it's the professional's own draft. */}
                    {isProfessional && !isMyDraft && (draft.user?.fullName || draft.user?.name) && (
                      <Chip bg="#e8efec" color="#145f5f" title="This document was shared with you">
                        Shared by {draft.user.fullName || draft.user.name}
                      </Chip>
                    )}

                    {/* Sign-off status: only once a document has left "draft" */}
                    {draft.signoff?.status && draft.signoff.status !== 'draft' && (
                      <Chip
                        bg={statusView(draft.signoff).bg}
                        color={statusView(draft.signoff).color}
                        title={statusView(draft.signoff).hint}
                      >
                        {statusView(draft.signoff).label}
                      </Chip>
                    )}

                    {/* Action buttons list */}
                    <div className="flex items-center gap-1.5 ml-2">
                      <Button
                        size="sm"
                        iconOnly
                        className="cs-btn--header-action"
                        onClick={() => handleView(draft)}
                        loading={loadingViewId === draft._id}
                        title="View draft"
                      >
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                          <circle cx="12" cy="12" r="3" />
                        </svg>
                      </Button>
                      <Button
                        size="sm"
                        iconOnly
                        className="cs-btn--header-action"
                        onClick={() => navigate(ROUTES.AGENT, { state: { openDraftId: draft._id } })}
                        title="Ask AI about this document"
                      >
                        {/* sparkle — opens the draft as an artifact in the AI chat */}
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M12 3l1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9L12 3z" />
                          <path d="M19 15l.9 2.1L22 18l-2.1.9L19 21l-.9-2.1L16 18l2.1-.9L19 15z" />
                        </svg>
                      </Button>
                      <Button
                        size="sm"
                        iconOnly
                        className="cs-btn--header-action"
                        onClick={() => setSigningDraft(draft)}
                        title="Review and sign"
                      >
                        {/* check-in-badge: checked and signed */}
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M9 12l2 2 4-4" />
                          <path d="M12 3l2.4 1.6 2.9-.2 1 2.7 2.4 1.7-.9 2.8.9 2.8-2.4 1.7-1 2.7-2.9-.2L12 21l-2.4-1.6-2.9.2-1-2.7-2.4-1.7.9-2.8-.9-2.8 2.4-1.7 1-2.7 2.9.2L12 3z" />
                        </svg>
                      </Button>
                      {draft.visibility !== 'personal' && isMyDraft && (
                        <Button
                          size="sm"
                          iconOnly
                          className="cs-btn--header-action"
                          onClick={() => setSharingDraft(draft)}
                          title="Share with a professional"
                        >
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                            <circle cx="8.5" cy="7" r="4" />
                            <line x1="20" y1="8" x2="20" y2="14" />
                            <line x1="23" y1="11" x2="17" y2="11" />
                          </svg>
                        </Button>
                      )}
                      {(!isProfessional || isMyDraft) && (
                        <Button
                          size="sm"
                          iconOnly
                          className="cs-btn--header-action"
                          onClick={() => handleDeleteClick(draft)}
                          disabled={isDeleting}
                          title="Delete draft"
                        >
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="3 6 5 6 21 6" />
                            <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
                            <line x1="10" y1="11" x2="10" y2="17" />
                            <line x1="14" y1="11" x2="14" y2="17" />
                          </svg>
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      {/* ── Pagination ────────────────────────────────────────────── */}
      {pagination.pages > 1 && (
        <div className="flex items-center justify-between mt-4 text-[12px]" style={{ color: '#7a7060' }}>
          <span>{pagination.total} document{pagination.total !== 1 ? 's' : ''}</span>
          <div className="flex gap-[6px]">
            <button
              disabled={!pagination.hasPrev}
              className="px-3 py-[5px] rounded-[6px] border text-[11.5px] font-semibold disabled:opacity-40 transition-colors hover:bg-[#f8f5ef]"
              style={{ borderColor: '#ddd6c6' }}
              onClick={() => setPage((p) => p - 1)}
            >
              Previous
            </button>
            <span className="px-3 py-[5px] text-[11.5px] font-semibold">
              Page {page} of {pagination.pages}
            </span>
            <button
              disabled={!pagination.hasNext}
              className="px-3 py-[5px] rounded-[6px] border text-[11.5px] font-semibold disabled:opacity-40 transition-colors hover:bg-[#f8f5ef]"
              style={{ borderColor: '#ddd6c6' }}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* ── View modals (documents & resolutions) ─────────────────── */}
      {viewingDoc?.mode === 'document' && (
        <DocumentDraftModal
          selected={{ doc: { id: viewingDoc.draft.documentId, title: viewingDoc.draft.documentTitle, icon: '📄', section: '', category: viewingDoc.draft.documentType } }}
          open={true}
          onClose={closeViewModal}
          contentLoading={viewingDoc.loading}
          initialDraft={viewingDoc.loading ? null : {
            _id: viewingDoc.draft._id,
            user: viewingDoc.draft.user,
            visibility: viewingDoc.draft.visibility,
            documentTitle: viewingDoc.draft.documentTitle,
            documentType: viewingDoc.draft.documentType,
            companyName: viewingDoc.draft.companyName,
            docHtml: viewingDoc.draft.docHtml,
            docSource: viewingDoc.draft.docSource,
            draftMeta: viewingDoc.draft.draftMeta,
            refNumber: viewingDoc.draft.refNumber,
          }}
          companyCin={viewingDoc.draft.companyCin}
          companyName={viewingDoc.draft.companyName}
          canEdit={viewingDoc.draft.canEdit}
          pageOrigin={viewingDoc.draft.pageOrigin || 'document-library'}
        />
      )}

      {viewingDoc?.mode === 'resolution' && (
        <ResolutionModal
          item={{ id: viewingDoc.draft.documentId, title: viewingDoc.draft.documentTitle, icon: '📜', subtitle: '' }}
          open={true}
          onClose={closeViewModal}
          contentLoading={viewingDoc.loading}
          initialDraft={viewingDoc.loading ? null : {
            _id: viewingDoc.draft._id,
            user: viewingDoc.draft.user,
            visibility: viewingDoc.draft.visibility,
            documentTitle: viewingDoc.draft.documentTitle,
            documentType: viewingDoc.draft.documentType,
            companyName: viewingDoc.draft.companyName,
            docHtml: viewingDoc.draft.docHtml,
            docSource: viewingDoc.draft.docSource,
            draftMeta: viewingDoc.draft.draftMeta,
            refNumber: viewingDoc.draft.refNumber,
          }}
          companyCin={viewingDoc.draft.companyCin}
          companyName={viewingDoc.draft.companyName}
          canEdit={viewingDoc.draft.canEdit}
        />
      )}

      <SignoffModal
        open={Boolean(signingDraft)}
        draft={signingDraft}
        isProfessional={isProfessional}
        onClose={() => setSigningDraft(null)}
        onChanged={handleSignoffChanged}
      />

      {/* ── Assign Professional Modal ─────────────────────────────────── */}
      <ShareDraftModal
        open={Boolean(sharingDraft)}
        draft={sharingDraft}
        onClose={() => setSharingDraft(null)}
        onChanged={handleShareChanged}
      />

      <ConfirmDialog
        open={Boolean(confirmDelete)}
        onClose={() => setConfirmDelete(null)}
        variant="danger"
        title="Delete Draft?"
        message={confirmDelete ? (
          <>
            Confirming will permanently delete <strong>{confirmDelete.documentTitle}</strong>.
          </>
        ) : ''}
        note="You can always regenerate this document fresh from the original template."
        confirmLabel="Delete"
        cancelLabel="Keep it"
        loading={Boolean(deletingId)}
        onConfirm={executeDelete}
      />
    </AppLayout>
  );
}
